import { useEffect, useRef, useState } from "react";
import { embedUrl } from "@/services/api";
import type { Video } from "@/types/video";

interface PlayerProps {
  video: Video | null;
  autoplay?: boolean;
}

// Bilibili embed player wrapped in a 16:9 container.
// Uses the official `player.bilibili.com/player.html` endpoint.
// Per spec #32, no video is proxied — Bilibili serves it directly.
//
// Failure detection strategy (fix for false-positive SIGNAL LOST):
//   - Iframe onLoad event  → mark as loaded, CANCEL the watchdog timer
//   - Iframe onError event → mark as failed, show SIGNAL LOST
//   - Watchdog timer (15s) → ONLY fires if NEITHER event has fired yet
//                            (real failure: network error, iframe never
//                            responded at all)
//
// This means: a video that's playing successfully will NEVER trigger
// SIGNAL LOST, regardless of how long it plays.

const WATCHDOG_MS = 15_000;

type LoadState = "pending" | "loaded" | "failed";

function isMobilePlaybackDevice(): boolean {
  const userAgent = navigator.userAgent;
  const mobileUserAgent = /Android|iPhone|iPod|Mobile/i.test(userAgent);
  const desktopModeIPad = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
  return mobileUserAgent || desktopModeIPad;
}

export default function Player({
  video,
  autoplay = true,
}: PlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loadState, setLoadState] = useState<LoadState>("pending");
  const mobilePlayback = isMobilePlaybackDevice();

  // Reset state when video changes
  useEffect(() => {
    setLoadState("pending");
  }, [video?.bvid]);

  // Watchdog: only fires if the iframe has produced no event within
  // WATCHDOG_MS. Once onLoad fires, loadState becomes "loaded" and the
  // condition `prev === "pending"` is false — so this never re-fires.
  useEffect(() => {
    if (!video) return;
    const timer = window.setTimeout(() => {
      setLoadState((prev) => (prev === "pending" ? "failed" : prev));
    }, WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [video]);

  if (!video) return null;

  if (loadState === "failed") {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center z-30 bg-void">
        <div className="vfd text-base animate-pulse">SIGNAL LOST</div>
        <p className="text-static text-xs font-mono tracking-wide opacity-70">
          此频道暂不可用
        </p>
        <div className="font-mono text-[10px] text-dim tracking-widest2 mt-2 opacity-60">
          按 SPACE / → 换台　|　按 ← 返回上一个
        </div>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      key={video.bvid}
      src={embedUrl(
        video.bvid,
        autoplay,
        mobilePlayback
      )}
      className="absolute inset-0 w-full h-full border-0 bg-void"
      allow="autoplay *; encrypted-media *; fullscreen *; picture-in-picture *"
      allowFullScreen
      referrerPolicy="no-referrer"
      loading="eager"
      title={`Bilibili ${video.bvid}`}
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox allow-presentation"
      onLoad={() => setLoadState("loaded")}
      onError={() => setLoadState("failed")}
    />
  );
}
